<template>
  <div data-testid="tq2-builder">
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">Tax Question V2</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ configLabel || today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-form-select</v-icon>
                Question builder
              </div>
              <p class="text-caption text-grey mt-1 d-flex align-center flex-wrap ga-2">
                <span>{{ headerLabel }}</span>
                <v-chip
                  v-if="versionActive"
                  data-testid="tq2-active-chip"
                  color="success"
                  variant="tonal"
                  size="x-small"
                  prepend-icon="mdi-check-circle"
                >
                  Active
                </v-chip>
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap align-center justify-end ga-2">
              <v-chip
                v-if="dirty"
                data-testid="tq2-unsaved-chip"
                color="warning"
                variant="tonal"
                size="small"
                prepend-icon="mdi-content-save-alert"
              >
                Unsaved changes
              </v-chip>
              <!-- Inline card previews for every card-kind row at once. A page-level switch, not one
                   per row: the reason to turn it on is to COMPARE the cards of a version, which a
                   per-row control cannot do. The value lives in the store's prefs (localStorage) so
                   it survives a reload — 🔴 and flipping it must never touch `dirty`: looking at the
                   cards is not an edit, and marking the document dirty here would put the "ยังไม่ได้
                   บันทึก" confirm in front of an admin who only read the page. -->
              <v-switch
                v-model="showCardPreview"
                data-testid="tq2-toggle-preview"
                class="tq2-preview-switch"
                label="แสดงตัวอย่างการ์ด"
                color="primary"
                density="compact"
                hide-details
              />
              <!-- Save history: every SAVE snapshots the whole question set, so this is the way back
                   to any earlier round (compare + restore live inside the dialog). ADD mode has no
                   real questionVersionId yet, hence nothing to list. -->
              <v-btn
                data-testid="tq2-history"
                variant="outlined"
                color="primary"
                size="large"
                rounded="lg"
                prepend-icon="mdi-history"
                :disabled="isNewVersion || loading || saving"
                @click="openHistory"
              >
                ประวัติการบันทึก
                <v-tooltip activator="parent" location="top">
                  {{
                    isNewVersion
                      ? 'บันทึกชุดคำถามก่อน จึงจะมีประวัติ'
                      : 'ดูรอบการบันทึกย้อนหลัง เทียบ และกู้คืน'
                  }}
                </v-tooltip>
              </v-btn>
              <v-btn
                data-testid="tq2-back"
                variant="outlined"
                color="primary"
                size="large"
                rounded="lg"
                prepend-icon="mdi-arrow-left"
                @click="goBack"
              >
                Back
              </v-btn>
              <v-btn
                ref="headerSave"
                data-testid="tq2-save"
                color="primary"
                variant="flat"
                size="large"
                rounded="lg"
                prepend-icon="mdi-content-save"
                :loading="saving"
                :disabled="saving || loading"
                @click="saveAll"
              >
                SAVE
                <v-tooltip activator="parent" location="top">บันทึกทั้งชุด (steps + items)</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- LIVE guard: persistent banner while editing the active version. -->
        <v-card-text v-if="versionActive" class="pt-0">
          <v-alert
            data-testid="tq2-live-banner"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>
        </v-card-text>

        <!-- Add-mode: new question-version metadata (created on SAVE). -->
        <v-card-text v-if="isNewVersion" class="pt-0">
          <div class="form-section">
            <h4 class="section-title">ข้อมูลชุดคำถามใหม่</h4>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="newVersionCode"
                  data-testid="tq2-new-code"
                  label="Version code *"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-tag-outline"
                  placeholder="Q1"
                  :rules="[rules.required]"
                  @update:model-value="newVersionCode = String(newVersionCode || '').toUpperCase()"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="newDescription"
                  data-testid="tq2-new-desc"
                  label="Description"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-text-box-outline"
                  placeholder="ชุดคำถาม PND90 ปี 2569"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="newResumePromptTh"
                  data-testid="tq2-new-resume"
                  label="Resume prompt (ข้อความต้อนรับกลับ)"
                  variant="outlined"
                  density="comfortable"
                  rows="2"
                  auto-grow
                  hide-details
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <!-- CALCULATE formula-health warnings (read path returns only these — see TaxQuestionItemV2BAL.GetFullAsync) -->
        <v-card-text v-if="warnings.length" class="pt-0">
          <v-alert data-testid="tq2-warning-alert" type="warning" variant="tonal" density="comfortable">
            <div class="font-weight-medium mb-1">คำเตือนสูตรคำนวณ (CALCULATE) — สูตรที่ยังรันไม่ได้จนกว่าจะแก้ที่ Tax Calculate V2</div>
            <ul class="tq2-warning-list">
              <li v-for="(w, i) in warnings" :key="i">{{ w }}</li>
            </ul>
          </v-alert>
        </v-card-text>

        <!-- Loading skeleton -->
        <v-card-text v-if="loading">
          <v-skeleton-loader type="table-tbody" />
        </v-card-text>

        <!-- Empty builder — no sections yet -->
        <div v-else-if="!sections.length" class="no-data-container" data-testid="tq2-empty-group">
          <div class="no-data-content">
            <v-icon size="64" color="grey-lighten-2">mdi-form-select</v-icon>
            <div class="no-data-text">
              <h3 class="no-data-title">ยังไม่มี Section</h3>
              <p class="no-data-subtitle">กด + เพิ่ม Section เพื่อเริ่มสร้างชุดคำถาม</p>
            </div>
            <v-btn data-testid="tq2-add-section" color="primary" variant="flat" rounded="lg" prepend-icon="mdi-plus" @click="openAddSection">
              เพิ่ม Section
            </v-btn>
          </div>
        </div>

        <!-- Sections -->
        <v-card-text v-else>
          <draggable
            v-model="draggableSections"
            item-key="code"
            handle=".tq2-section-handle"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            :animation="180"
            tag="div"
          >
            <template #item="{ element: group, index: sIdx }">
              <v-card :data-testid="`tq2-section-${group.code}`" variant="outlined" class="mb-4 tq2-group-card">
                <div class="tq2-group-header">
                  <div class="d-flex align-center ga-2 flex-grow-1 tq2-section-headrow">
                    <v-btn
                      :data-testid="`tq2-section-drag-${group.code}`"
                      class="tq2-section-handle"
                      variant="text"
                      density="comfortable"
                      icon
                      aria-label="Drag to reorder section"
                    >
                      <v-icon size="20">mdi-drag-vertical</v-icon>
                      <v-tooltip activator="parent" location="top">ลากเพื่อจัดลำดับ</v-tooltip>
                    </v-btn>
                    <v-btn
                      :data-testid="`tq2-section-collapse-${group.code}`"
                      variant="text"
                      density="comfortable"
                      icon
                      aria-label="Collapse or expand section"
                      @click="toggleSection(group.code)"
                    >
                      <v-icon size="20">{{ isSectionExpanded(group.code) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      <v-tooltip activator="parent" location="top">{{ isSectionExpanded(group.code) ? 'หุบ Section' : 'กาง Section' }}</v-tooltip>
                    </v-btn>
                    <v-text-field
                      :data-testid="`tq2-section-name-${group.code}`"
                      :model-value="group.label"
                      class="tq2-section-name-field"
                      variant="plain"
                      density="compact"
                      hide-details
                      :disabled="!group.meta"
                      placeholder="ชื่อ Section"
                      aria-label="Section name"
                      @update:model-value="onSectionLabel(group.code, $event)"
                    />
                    <span class="tq2-group-count">{{ group.rows.length }} คำถาม</span>
                  </div>
                  <div class="d-flex align-center ga-1">
                    <v-btn variant="text" density="comfortable" icon :disabled="sIdx === 0 || !group.meta" aria-label="Move section up" @click="moveSection(sIdx, -1)">
                      <v-icon size="18">mdi-arrow-up</v-icon>
                      <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                    </v-btn>
                    <v-btn variant="text" density="comfortable" icon :disabled="sIdx === sections.length - 1 || !group.meta" aria-label="Move section down" @click="moveSection(sIdx, 1)">
                      <v-icon size="18">mdi-arrow-down</v-icon>
                      <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                    </v-btn>
                    <v-btn
                      :data-testid="`tq2-section-settings-${group.code}`"
                      variant="text"
                      density="comfortable"
                      icon
                      :color="expandedSettings[group.code] ? 'primary' : undefined"
                      :disabled="!group.meta"
                      aria-label="ตั้งค่าขั้นสูง"
                      @click="toggleSettings(group.code)"
                    >
                      <v-icon size="18">mdi-cog-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ตั้งค่าขั้นสูง</v-tooltip>
                    </v-btn>
                    <v-btn v-if="group.meta" variant="text" density="comfortable" icon class="delete-btn" aria-label="Delete section" @click="deleteSection(group.code, group.label)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                      <v-tooltip activator="parent" location="top">ลบ Section</v-tooltip>
                    </v-btn>
                  </div>
                </div>

                <!-- Section body (collapsible): advanced settings + item table + footer. -->
                <v-expand-transition>
                <div v-if="isSectionExpanded(group.code)" :data-testid="`tq2-section-body-${group.code}`">

                <!-- Advanced settings -->
                <v-expand-transition>
                  <div v-if="group.meta && expandedSettings[group.code]" class="tq2-step-meta">
                    <div class="text-caption font-weight-medium text-grey mb-2">
                      <v-icon size="16" class="mr-1">mdi-tune-variant</v-icon>ตั้งค่าขั้นสูง
                    </div>
                    <v-textarea
                      :data-testid="`tq2-step-qprompt-${group.code}`"
                      :model-value="group.meta.questionSystemPrompt"
                      label="คำสั่ง AI ของหมวด — ตอนถาม (Question)"
                      variant="outlined"
                      density="comfortable"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mt-3"
                      @update:model-value="updateMeta(group.code, 'questionSystemPrompt', $event)"
                    />
                    <!-- 🔴 ORDER MIRRORS TaxQuestionItemV2Dialog, one level up — question prompt → its retrieval
                         query → its Knowledge, THEN answer prompt → its Knowledge. Grouping by op rather than
                         putting the two prompts together is the point: each picker sits under the prompt it feeds,
                         so "which Knowledge is this?" is answered by position instead of by reading the label.
                         The section's lists apply to every question under it and a question's own lists are
                         ADDITIVE to them; the query is a section-wide default a question may override. -->
                    <v-textarea
                      :data-testid="`tq2-step-kquery-${group.code}`"
                      :model-value="group.meta.questionKnowledgeQuery"
                      label="คำค้นเอกสารของหมวด — ตอนปรุงคำถาม (retrieve doc for question)"
                      placeholder="เว้นว่าง = ใช้คำค้นของคำถาม หรือตัวคำถามเอง"
                      variant="outlined"
                      density="comfortable"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mt-3"
                      @update:model-value="updateMeta(group.code, 'questionKnowledgeQuery', $event)"
                    />
                    <!-- Section Knowledge is picked by SUBCATEGORY (DocumentCategory → Title, e.g.
                         Tax → Personal Income Tax): the "Category:Title" token is stored and resolved to its
                         documents LIVE at generation time, so a doc ingested into the subcategory later is
                         included without re-saving. The per-doc pickers below only render while they still
                         hold legacy selections (still merged into the prompt): chip-remove + save to migrate
                         a section, and the picker disappears. -->
                    <v-autocomplete
                      :data-testid="`tq2-step-kcat-question-${group.code}`"
                      :model-value="group.meta.questionKnowledgeCategories"
                      :items="categoryCatalog"
                      item-title="title"
                      item-value="value"
                      label="หมวดย่อยเอกสาร (Knowledge) ของหมวด — สำหรับปรุงคำถาม"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-bookshelf"
                      multiple
                      chips
                      closable-chips
                      hide-details
                      no-data-text="ไม่พบหมวดย่อยเอกสาร"
                      class="mt-3"
                      @update:model-value="onStepKnowledge(group.code, 'questionKnowledgeCategories', $event)"
                    />
                    <v-autocomplete
                      v-if="(group.meta.questionKnowledgeIds || []).length"
                      :data-testid="`tq2-step-knowledge-question-${group.code}`"
                      :model-value="group.meta.questionKnowledgeIds"
                      :items="knowledgeCatalog"
                      item-title="title"
                      item-value="knowledgeCode"
                      label="Knowledge ของหมวด — สำหรับปรุงคำถาม (รายเอกสาร — เดิม)"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-book-open-variant"
                      multiple
                      chips
                      closable-chips
                      hide-details
                      no-data-text="ไม่พบ Knowledge"
                      class="mt-3"
                      @update:model-value="onStepKnowledge(group.code, 'questionKnowledgeIds', $event)"
                    />
                    <v-textarea
                      :data-testid="`tq2-step-aprompt-${group.code}`"
                      :model-value="group.meta.answerSystemPrompt"
                      label="คำสั่ง AI ของหมวด — ตอนสกัดคำตอบ (Answer)"
                      variant="outlined"
                      density="comfortable"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mt-3"
                      @update:model-value="updateMeta(group.code, 'answerSystemPrompt', $event)"
                    />
                    <v-autocomplete
                      :data-testid="`tq2-step-kcat-answer-${group.code}`"
                      :model-value="group.meta.answerKnowledgeCategories"
                      :items="categoryCatalog"
                      item-title="title"
                      item-value="value"
                      label="หมวดย่อยเอกสาร (Knowledge) ของหมวด — สำหรับสกัดคำตอบ"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-bookshelf"
                      multiple
                      chips
                      closable-chips
                      hide-details
                      no-data-text="ไม่พบหมวดย่อยเอกสาร"
                      class="mt-3"
                      @update:model-value="onStepKnowledge(group.code, 'answerKnowledgeCategories', $event)"
                    />
                    <v-autocomplete
                      v-if="(group.meta.answerKnowledgeIds || []).length"
                      :data-testid="`tq2-step-knowledge-answer-${group.code}`"
                      :model-value="group.meta.answerKnowledgeIds"
                      :items="knowledgeCatalog"
                      item-title="title"
                      item-value="knowledgeCode"
                      label="Knowledge ของหมวด — สำหรับสกัดคำตอบ (รายเอกสาร — เดิม)"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-book-open-variant"
                      multiple
                      chips
                      closable-chips
                      hide-details
                      no-data-text="ไม่พบ Knowledge"
                      class="mt-3"
                      @update:model-value="onStepKnowledge(group.code, 'answerKnowledgeIds', $event)"
                    />
                  </div>
                </v-expand-transition>

                <!-- Empty section -->
                <div v-if="!group.rows.length" class="tq2-empty-section">
                  ยังไม่มีคำถาม — กด + เพิ่มคำถาม
                </div>

                <v-table v-else density="comfortable" class="tq2-item-table">
                  <thead>
                    <tr>
                      <th style="width: 128px;">Order</th>
                      <th>Opening question</th>
                      <th style="width: 150px;">Type</th>
                      <th style="width: 84px;" class="text-center">Active</th>
                      <th style="width: 90px;" class="text-center">Required</th>
                      <th style="width: 170px;">Visible when</th>
                      <!-- min-width as well as width: the table lays out automatically, so a plain
                           width is only a hint the browser will shrink back when a page happens to
                           hold short chips. These labels are full sentences and need the room. -->
                      <th style="width: 320px; min-width: 320px;">Quick reply</th>
                      <th style="width: 160px;" class="text-center">Action</th>
                    </tr>
                  </thead>
                  <draggable
                    :list="group.visibleRows"
                    item-key="_uid"
                    tag="tbody"
                    handle=".tq2-item-handle"
                    ghost-class="drag-ghost"
                    chosen-class="drag-chosen"
                    :animation="180"
                    @change="onItemReorder(group)"
                  >
                    <template #item="{ element: row, index: idx }">
                      <tr :key="row._uid" :data-testid="`tq2-item-row-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`">
                        <td>
                          <div class="d-flex align-center ga-1">
                            <!-- Sequential position within this section. Derived from the render
                                 index rather than stored, so it stays correct the moment a row is
                                 dragged, moved, added or removed — no re-numbering pass needed. -->
                            <span class="tq2-item-seq">{{ group.seqOffset + idx + 1 }}</span>
                            <v-btn
                              :data-testid="`tq2-item-drag-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                              class="tq2-item-handle"
                              size="x-small"
                              variant="text"
                              icon
                              aria-label="Drag to reorder item"
                            >
                              <v-icon size="18">mdi-drag-vertical</v-icon>
                            </v-btn>
                            <v-btn size="x-small" variant="text" icon :disabled="idx === 0" aria-label="Move item up" @click="moveItem(group, idx, -1)">
                              <v-icon size="18">mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn size="x-small" variant="text" icon :disabled="idx === group.visibleRows.length - 1" aria-label="Move item down" @click="moveItem(group, idx, 1)">
                              <v-icon size="18">mdi-arrow-down</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td>
                          <div class="tq2-item-cell">
                            <div class="d-flex align-center ga-2 flex-wrap">
                              <v-chip :color="kindColor(row.itemKind)" variant="tonal" size="x-small">{{ row.itemKind }}</v-chip>
                              <strong>{{ questionPreview(row) }}</strong>
                              <!-- SAVE_STATE pin ticked «หลังแก้แผนที่จบแล้ว ให้กลับมาที่ฉากนี้» (postCompleteEditReturn) —
                                   shown inline so an admin can see at a glance which pin (at most one per set) the
                                   post-completion edit round walks back to. Kept in this flex row, outside the
                                   per-kind v-if chain below, so it cannot detach that chain. -->
                              <v-chip
                                v-if="row.itemKind === 'SAVE_STATE' && row.postCompleteEditReturn"
                                :data-testid="`tq2-item-post-complete-edit-return-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                                color="deep-purple"
                                variant="tonal"
                                size="x-small"
                                label
                              >
                                <v-icon start size="14">mdi-keyboard-return</v-icon>
                                กลับมาฉากนี้หลังแก้
                                <v-tooltip activator="parent" location="top">
                                  ลูกค้าขอแก้ข้อที่เคยตอบบนแผนที่จบแล้ว ⇒ ถามเฉพาะข้อนั้น แล้วพากลับมาที่ฉากของหมุดนี้
                                </v-tooltip>
                              </v-chip>
                              <!-- «คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)» (editKeywords) — how many words the edit-request
                                   matcher counts as names of this item. Same flex row as the chip above, outside the
                                   per-kind v-if chain below. -->
                              <v-chip
                                v-if="Array.isArray(row.editKeywords) && row.editKeywords.length"
                                :data-testid="`tq2-item-edit-keywords-badge-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                                color="teal"
                                variant="tonal"
                                size="x-small"
                                label
                              >
                                <v-icon start size="14">mdi-tag-text-outline</v-icon>
                                คำเรียก {{ row.editKeywords.length }}
                                <!-- SUMMARY: คำเรียกใช้ดึงการ์ดสรุปมาแสดง (ไม่ใช่เป้าของการขอแก้) — ป้ายนำหน้าจึงต่างจาก kind อื่น -->
                                <v-tooltip activator="parent" location="top">
                                  {{ row.itemKind === 'SUMMARY' ? 'คำที่ลูกค้าใช้เรียกการ์ดนี้ (ดึงการ์ดสรุป)' : 'คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)' }}: {{ row.editKeywords.join(' · ') }}
                                </v-tooltip>
                              </v-chip>
                            </div>
                            <!-- INCOME_CLASSIFIER จำแนกรายได้ → สรุปจำนวนประเภท + list flagKey ย่อ -->
                            <span v-if="row.itemKind === 'INCOME_CLASSIFIER'" class="tq2-group-summary">
                              <v-chip color="warning" variant="tonal" size="x-small" label>
                                <v-icon start size="14">mdi-shape-outline</v-icon>
                                จำแนก {{ (row.incomeCategories || []).length }} ประเภท
                              </v-chip>
                              <span v-if="(row.incomeCategories || []).length" class="tq2-item-key tq2-group-keys">
                                {{ incomeCategoryKeys(row) }}
                                <v-tooltip activator="parent" location="top">{{ incomeCategoryKeys(row) }}</v-tooltip>
                              </span>
                            </span>
                            <!-- INCOME_CLASSIFY คุมบทสนทนารายได้ → จำนวนหมวด + ปลายทางที่ยอดไปลง
                                 (ตัวข้อไม่เก็บยอดเอง เหมือน SHORT_FORM — คีย์ที่ขึ้นตรงนี้คือข้อ §40 ปลายทาง) -->
                            <span
                              v-else-if="row.itemKind === 'INCOME_CLASSIFY'"
                              :data-testid="`tq2-item-incomeclassify-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                              class="tq2-group-summary"
                            >
                              <v-chip color="deep-orange" variant="tonal" size="x-small" label>
                                <v-icon start size="14">mdi-format-list-numbered</v-icon>
                                คุม {{ (row.incomeCategories || []).length }} หมวด
                              </v-chip>
                              <span v-if="incomeFanOutKeys(row)" class="tq2-item-key tq2-group-keys">
                                → {{ incomeFanOutKeys(row) }}
                                <v-tooltip activator="parent" location="top">{{ incomeFanOutKeysFull(row) }}</v-tooltip>
                              </span>
                            </span>
                            <!-- The §40 rows this driver owns, nested INSIDE it. They used to sit in the table as
                                 peers, which read as thirteen unrelated questions when it is one conversation plus
                                 the rows its answers land on — and invited someone to drag one above its driver,
                                 where it would be asked on its own before the conversation ever started. -->
                            <div
                              v-if="(group.ownedByUid[row._uid] || []).length"
                              :data-testid="`tq2-item-owned-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                              class="tq2-owned"
                            >
                              <div class="tq2-owned-head">
                                <v-icon size="14" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                คำถามที่ข้อนี้คุมอยู่ ({{ group.ownedByUid[row._uid].length }}) —
                                ไม่ถูกถามแยก แต่เป็นที่เก็บคำตอบและเจ้าของข้อความคำถาม
                              </div>
                              <div
                                v-for="owned in group.ownedByUid[row._uid]"
                                :key="owned.row._uid"
                                :data-testid="`tq2-item-owned-row-${owned.row.questionItemID != null ? owned.row.questionItemID : 'new-' + owned.row._uid}`"
                                class="tq2-owned-row"
                              >
                                <!-- Why this row is in here at all. A fan-out target and a follow-up question are
                                     owned for completely different reasons, and an admin who cannot tell them
                                     apart cannot tell which ones the catalog above controls. -->
                                <v-icon
                                  size="13"
                                  :class="['tq2-owned-why', owned.why === 'target' ? 'is-target' : 'is-followup']"
                                >
                                  {{ owned.why === 'target' ? 'mdi-target' : 'mdi-comment-question-outline' }}
                                  <v-tooltip activator="parent" location="top">
                                    {{ owned.why === 'target' ? 'ปลายทางที่ยอดของหมวดนี้ไปลง' : 'คำถามต่อเนื่องของบทสนทนานี้ (ถามย้ำว่ามีรายได้หมวดอื่นอีกไหม)' }}
                                  </v-tooltip>
                                </v-icon>
                                <span class="tq2-item-key tq2-owned-key">{{ owned.row.fieldKey }}</span>
                                <span class="tq2-owned-text">{{ questionPreview(owned.row) }}</span>
                                <span v-if="!isAlwaysVisible(owned.row)" class="tq2-owned-gate">{{ visibleWhenLabel(owned.row) }}</span>
                                <v-btn
                                  size="x-small"
                                  density="comfortable"
                                  variant="text"
                                  icon
                                  aria-label="Edit item"
                                  :data-testid="`tq2-item-owned-edit-${owned.row.questionItemID != null ? owned.row.questionItemID : 'new-' + owned.row._uid}`"
                                  @click="openEditDialog(owned.row)"
                                >
                                  <v-icon size="16">mdi-pencil</v-icon>
                                  <v-tooltip activator="parent" location="top">แก้ข้อความ/กฎของข้อนี้</v-tooltip>
                                </v-btn>
                              </div>
                            </div>
                            <!-- CALCULATE คำนวณ → สรุปจำนวนสูตร + รายชื่อ functionCode ย่อ -->
                            <span v-else-if="row.itemKind === 'CALCULATE'" class="tq2-group-summary">
                              <v-chip color="deep-purple" variant="tonal" size="x-small" label>
                                <v-icon start size="14">mdi-calculator-variant</v-icon>
                                {{ calcFunctionCount(row) }} สูตร
                              </v-chip>
                              <span v-if="calcFunctionKeys(row)" class="tq2-item-key tq2-group-keys">
                                {{ calcFunctionKeys(row) }}
                                <v-tooltip activator="parent" location="top">{{ calcFunctionKeysFull(row) }}</v-tooltip>
                              </span>
                            </span>
                            <!-- SHORT_FORM ฟอร์มสั้น → สรุปจำนวนช่องที่การ์ดเก็บ + รายชื่อ FieldKey ปลายทางย่อ
                                 (ตัวการ์ดไม่เก็บยอดเอง — ยอดไปลงคำถาม FIELD_INPUT ที่ชื่อเหล่านี้ชี้ไป) -->
                            <span
                              v-else-if="row.itemKind === 'SHORT_FORM'"
                              :data-testid="`tq2-item-shortform-${row.questionItemID != null ? row.questionItemID : 'new-' + row._uid}`"
                              class="tq2-group-summary"
                            >
                              <v-chip color="cyan" variant="tonal" size="x-small" label>
                                <v-icon start size="14">mdi-card-text-outline</v-icon>
                                {{ shortFormFieldCount(row) }} ช่อง
                              </v-chip>
                              <span v-if="shortFormKeys(row)" class="tq2-item-key tq2-group-keys">
                                {{ shortFormKeys(row) }}
                                <v-tooltip activator="parent" location="top">{{ shortFormKeysFull(row) }}</v-tooltip>
                              </span>
                            </span>
                            <!-- USAGE_FUNNEL_STATE (#746) → ชื่อ state ที่ข้อนี้นับให้ใน Usage Funnel ของ TaxDashboard (inline
                                 ให้ไล่ดูได้ว่าวางครบ 8 state โดยไม่ต้อง hover) -->
                            <span v-else-if="row.itemKind === 'USAGE_FUNNEL_STATE'" class="tq2-group-summary">
                              <v-chip :color="row.stateGroup ? 'blue' : 'error'" variant="tonal" size="x-small" label>
                                <v-icon start size="14">mdi-filter-variant</v-icon>
                                {{ usageFunnelStateTitle(row) }}
                              </v-chip>
                            </span>
                            <span v-else class="tq2-item-key">{{ row.fieldKey || '' }}</span>
                          </div>
                          <!-- 🔴 Inside the EXISTING cell, after `.tq2-item-cell` closes — never a second
                               `<tr>`. vuedraggable v4 allows exactly one root element per `#item`
                               template, and a sibling row would desynchronise Sortable's indexes from
                               the list it reorders. Outside `.tq2-item-cell` because that box is a
                               flex column of one-line summaries; the card is a block of its own.
                               Gated twice on purpose: here, so a row that draws no card leaves NOTHING
                               behind — not even an empty wrapper — in a table that renders every item of
                               every section; and again inside the component, which is what makes it safe
                               to mount anywhere. Both ask `utils/taxCardPreview.js`; neither restates the
                               kind list. -->
                          <div
                            v-if="showCardPreview && hasCardPreview(row.itemKind)"
                            class="tq2-inline-preview-wrap"
                          >
                            <TaxCardInlinePreviewV2
                              :item="row"
                              :enabled="showCardPreview"
                              :testid-suffix="row.questionItemID != null ? '-' + row.questionItemID : '-new-' + row._uid"
                            />
                          </div>
                        </td>
                        <td>
                          <v-chip
                            :color="answerKind(row).color"
                            :prepend-icon="answerKind(row).icon"
                            variant="tonal"
                            size="small"
                            label
                            class="tq2-answer-kind-chip"
                          >
                            {{ answerKind(row).label }}
                            <v-tooltip activator="parent" location="top">{{ answerKind(row).hint }}</v-tooltip>
                          </v-chip>
                        </td>
                        <!-- Mirrors the "เปิดใช้งาน" switch in the item dialog's
                             การแสดงผล & เงื่อนไข block. Shown here so a disabled question is
                             visible from the list instead of only after opening each row. -->
                        <td class="text-center">
                          <v-icon v-if="row.isActive" size="18" color="success">mdi-check-circle</v-icon>
                          <v-icon v-else size="18" color="error">mdi-close-circle</v-icon>
                          <v-tooltip activator="parent" location="top">
                            {{ row.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน' }}
                          </v-tooltip>
                        </td>
                        <td class="text-center">
                          <v-icon v-if="row.isRequired" size="18" color="success">mdi-check</v-icon>
                          <span v-else class="text-grey">-</span>
                        </td>
                        <td>
                          <span :class="['tq2-vw-cell', { 'tq2-vw-always': isAlwaysVisible(row) }]">
                            {{ visibleWhenLabel(row) }}
                            <v-tooltip v-if="!isAlwaysVisible(row)" activator="parent" location="top">
                              <span class="tq2-vw-raw">{{ row.visibleWhen }}</span>
                            </v-tooltip>
                          </span>
                        </td>
                        <!-- Thai labels only. The runtime renders a chip row in ONE language, and
                             falls back to Thai for the whole row unless every chip of the item is
                             translated and none collide — so Thai is the label that is always
                             actually shown, and the only one worth previewing here. -->
                        <td>
                          <ul v-if="quickReplyLabels(row).length" class="tq2-qr-list">
                            <li v-for="(label, i) in quickReplyLabels(row)" :key="i">{{ label }}</li>
                          </ul>
                          <span v-else class="text-grey">-</span>
                        </td>
                        <td class="text-center">
                          <div class="action-group">
                            <v-btn variant="text" density="comfortable" icon class="action-btn" aria-label="Edit item" @click="openEditDialog(row)">
                              <v-icon size="18">mdi-pencil-outline</v-icon>
                              <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                            </v-btn>
                            <v-btn variant="text" density="comfortable" icon class="action-btn delete-btn" aria-label="Delete item" @click="deleteItem(row)">
                              <v-icon size="18">mdi-delete-outline</v-icon>
                              <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                            </v-btn>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </draggable>
                </v-table>

                <div class="tq2-section-footer">
                  <v-btn :data-testid="`tq2-add-item-${group.code}`" size="small" variant="outlined" color="primary" prepend-icon="mdi-plus" @click="addItemToGroup(group.code)">
                    เพิ่มคำถาม
                  </v-btn>
                  <v-btn
                    :data-testid="`tq2-add-classifier-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="warning"
                    prepend-icon="mdi-shape-outline"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'INCOME_CLASSIFIER')"
                  >
                    เพิ่มตัวจำแนกรายได้
                  </v-btn>
                  <v-btn
                    :data-testid="`tq2-add-incomeclassify-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="deep-orange"
                    prepend-icon="mdi-format-list-numbered"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'INCOME_CLASSIFY')"
                  >
                    เพิ่มตัวคุมบทสนทนารายได้
                  </v-btn>
                  <v-btn
                    :data-testid="`tq2-add-confirm-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="teal"
                    prepend-icon="mdi-check-decagram-outline"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'CONFIRM')"
                  >
                    เพิ่มจุดยืนยัน/สรุป
                  </v-btn>
                  <v-btn
                    :data-testid="`tq2-add-calculate-${group.code}`"
                    size="small"
                    variant="outlined"
                    color="deep-purple"
                    prepend-icon="mdi-calculator-variant"
                    class="ml-2"
                    @click="addItemToGroup(group.code, 'CALCULATE')"
                  >
                    เพิ่มการคำนวณ
                  </v-btn>
                </div>

                </div>
                </v-expand-transition>
              </v-card>
            </template>
          </draggable>

          <div class="d-flex justify-center mt-2">
            <v-btn data-testid="tq2-add-section" color="primary" variant="tonal" rounded="lg" prepend-icon="mdi-plus" @click="openAddSection">
              เพิ่ม Section
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <TaxQuestionItemV2Dialog
      v-model="dialogOpen"
      :initial-item="dialogInitial"
      :config-version-id="configVersionId"
      :knowledge-catalog="knowledgeCatalog"
      :sibling-items="items"
      @submit="onDialogSubmit"
    />

    <!-- Pre-save "what changed" confirm dialog (+ LIVE acknowledgement) — ported from V1. -->
    <v-dialog v-model="confirmSaveDialog" max-width="560px" persistent data-testid="tq2-confirm-save-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-content-save-check-outline</v-icon>
          ยืนยันการบันทึก
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            v-if="versionActive"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
            class="mb-4"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (LIVE) — การบันทึกจะมีผลกับระบบที่ใช้งานอยู่
          </v-alert>

          <div class="d-flex flex-wrap ga-2 mb-3" data-testid="tq2-diff-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ saveDiff.added.length }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ saveDiff.edited.length }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-delete">
              ลบ {{ saveDiff.removed.length }}
            </v-chip>
          </div>

          <p
            v-if="!saveDiff.added.length && !saveDiff.edited.length && !saveDiff.removed.length"
            class="text-caption text-medium-emphasis mb-2"
          >
            ไม่พบการเปลี่ยนแปลง — การบันทึกจะเขียนทับชุดคำถามด้วยข้อมูลเดิม
          </p>

          <div v-if="saveDiff.added.length" class="mb-2">
            <div class="text-caption font-weight-medium text-success mb-1">เพิ่ม ({{ saveDiff.added.length }})</div>
            <ul class="tq2-diff-list">
              <li v-for="(it, i) in saveDiff.added.slice(0, 8)" :key="'a' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.added.length > 8" class="text-grey">…และอีก {{ saveDiff.added.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.edited.length" class="mb-2">
            <div class="text-caption font-weight-medium text-primary mb-1">แก้ไข ({{ saveDiff.edited.length }})</div>
            <ul class="tq2-diff-list">
              <li v-for="(it, i) in saveDiff.edited.slice(0, 8)" :key="'e' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.edited.length > 8" class="text-grey">…และอีก {{ saveDiff.edited.length - 8 }} รายการ</li>
            </ul>
          </div>
          <div v-if="saveDiff.removed.length" class="mb-2">
            <div class="text-caption font-weight-medium text-error mb-1">ลบ ({{ saveDiff.removed.length }})</div>
            <ul class="tq2-diff-list">
              <li v-for="(it, i) in saveDiff.removed.slice(0, 8)" :key="'r' + i">{{ diffItemLabel(it) }}</li>
              <li v-if="saveDiff.removed.length > 8" class="text-grey">…และอีก {{ saveDiff.removed.length - 8 }} รายการ</li>
            </ul>
          </div>

          <!-- 🔴 บันทึกช่วยจำของรอบนี้ — หน้าประวัติการบันทึกมีให้อ่านแค่ เวลา/คนเซฟ/จำนวนข้อ
               ข้อความตรงนี้จึงเป็นสิ่งเดียวที่บอกได้ว่า "รอบนั้นแก้อะไร" ตอนย้อนกลับมาดูทีหลัง -->
          <v-text-field
            v-model="saveComment"
            data-testid="tq2-save-comment"
            label="บันทึกช่วยจำ (ไม่บังคับ)"
            placeholder="รอบนี้แก้อะไร — ข้อความนี้จะแสดงในหน้าประวัติการบันทึก"
            variant="outlined"
            density="comfortable"
            maxlength="500"
            counter="500"
            clearable
            class="mt-2"
          />

          <v-checkbox
            v-if="versionActive"
            v-model="liveConfirmChecked"
            data-testid="tq2-live-ack"
            color="warning"
            density="comfortable"
            hide-details
            label="ฉันเข้าใจว่านี่คือเวอร์ชันที่ใช้งานจริง"
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="confirmSaveDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="tq2-confirm-save-btn"
            color="primary"
            variant="elevated"
            :disabled="versionActive && !liveConfirmChecked"
            @click="confirmSave"
          >
            <v-icon start>mdi-content-save</v-icon>
            ยืนยันบันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save history (list + compare + restore all live inside the dialog). -->
    <TaxQuestionHistoryV2Dialog
      v-model="historyDialog"
      :question-version-id="questionVersionId"
      :version-label="headerLabel"
      :version-active="versionActive"
      @restored="onHistoryRestored"
    />

    <!-- Unsaved-changes gate in front of the history dialog (a restore overwrites the builder). -->
    <v-dialog v-model="historyDirtyDialog" max-width="520px" persistent data-testid="tq2-history-dirty-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-content-save-alert</v-icon>
          ยังมีการแก้ไขที่ไม่ได้บันทึก
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" density="comfortable" icon="mdi-alert" class="mb-0">
            มีการแก้ไขที่ยังไม่ได้บันทึก — การกู้คืนเวอร์ชันจะเขียนทับชุดคำถามด้วยข้อมูลของรอบที่เลือก
            และการแก้ที่ค้างอยู่จะหายไป ต้องการเปิดประวัติต่อหรือไม่?
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="historyDirtyDialog = false">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="tq2-history-dirty-confirm"
            color="primary"
            variant="elevated"
            @click="confirmOpenHistory"
          >
            <v-icon start>mdi-history</v-icon>
            เปิดประวัติ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Section — name only -->
    <v-dialog v-model="addSectionDialog" max-width="480px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-plus-box-outline</v-icon>เพิ่ม Section ใหม่
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            data-testid="tq2-section-name-input"
            v-model="newSectionName"
            label="ชื่อ Section *"
            placeholder="เช่น ข้อมูลส่วนตัว / ประกันและประกันสังคม"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            autofocus
            @keyup.enter="confirmAddSection"
          />
          <p class="text-caption text-medium-emphasis mb-0">
            ใส่แค่ชื่อก็พอ — ระบบจะสร้างรหัส Section ให้อัตโนมัติ (ตั้งค่าอื่น ๆ แก้ได้ภายหลังใน "ตั้งค่าขั้นสูง")
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" @click="addSectionDialog = false">
            <v-icon start>mdi-close</v-icon>ยกเลิก
          </v-btn>
          <v-btn data-testid="tq2-section-create" color="primary" variant="elevated" @click="confirmAddSection">
            <v-icon start>mdi-plus</v-icon>เพิ่ม Section
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Floating SAVE — a question set runs far longer than one screen, so once the header button
         has scrolled away this one takes over instead of making people scroll back to the top.
         Same handler, same disabled rules; it only ever mirrors the header button. -->
    <transition name="tq2-fab">
      <div v-if="floatingSaveVisible" class="tq2-floating-save" data-testid="tq2-save-float-wrap">
        <v-chip
          v-if="dirty"
          data-testid="tq2-save-float-chip"
          color="warning"
          variant="elevated"
          size="small"
          prepend-icon="mdi-content-save-alert"
        >
          Unsaved
        </v-chip>
        <v-btn
          data-testid="tq2-save-float"
          color="primary"
          variant="elevated"
          size="large"
          rounded="pill"
          elevation="8"
          prepend-icon="mdi-content-save"
          :loading="saving"
          :disabled="saving || loading"
          @click="saveAll"
        >
          SAVE
        </v-btn>
      </div>
    </transition>

    <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" timeout="3000">
      {{ snackbarMessage }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <ConfirmDeleteDialog ref="confirmDelete" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { taxConfigV2Api } from '../services/taxConfigV2Api';
import { taxQuestionV2Api } from '../services/taxQuestionV2Api';
// 🔴 KIND_COLORS / ANSWER_KIND_META are IMPORTED, never re-declared here. The item dialog badges the same rows in
//    its fan-out preview, and a private copy on either side would let the two drift into disagreeing about what
//    one item is.
import {
  taxQuestionV2Store, shortFormFieldKeys, KIND_COLORS, ANSWER_KIND_META, USAGE_FUNNEL_STATE_OPTIONS,
} from '../utils/taxQuestionV2Store';
import { describeVisibleWhen, parseVisibleWhen } from '../utils/visibleWhen';
import { metaDocApi } from '../services/metaDocApi';
import TaxQuestionItemV2Dialog from '../components/TaxQuestionItemV2Dialog.vue';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog.vue';
// Save-history dialog owns the whole flow (list → compare → confirm → restore). The compare dialog is
// its own child, so this view never imports it — it only re-syncs the builder on the `restored` event.
import TaxQuestionHistoryV2Dialog from '../components/TaxQuestionHistoryV2Dialog.vue';
// The card preview as it appears in a table row. It gates itself on the item's kind and lazily
// renders once the row reaches the viewport, so the table can mount one per row unconditionally.
import TaxCardInlinePreviewV2 from '../components/TaxCardInlinePreviewV2.vue';
// 🔴 The ONE predicate for "does this kind draw a card" — imported, never re-listed. The dialog and the
//    inline preview gate on this same function; a private copy of the kind list here is exactly what
//    drifted last time. Aliased only so the template-facing method below can keep the canonical name.
import { hasCardPreview as itemKindHasCardPreview } from '../utils/taxCardPreview';

// How many functionCodes of a CALCULATE item to spell out in the table before
// collapsing the rest into "…" (the full list stays in the tooltip).
const CALC_CODE_PREVIEW = 3;

// Same budget again for an INCOME_CLASSIFY's fan-out targets. Its own constant for the same reason: a driver names
// one target per §40 section (up to eight) where a calc item rarely runs three formulas.
const INCOME_TARGET_PREVIEW = 3;

// Same budget for a SHORT_FORM's member FieldKeys. Kept separate from CALC_CODE_PREVIEW
// so the two summaries stay tunable on their own — a mini-form routinely names more
// targets than a calc item has formulas.
const SHORT_FORM_KEY_PREVIEW = 3;

// The fact an INCOME_CLASSIFY publishes that says HOW MANY §40 categories it found. Only the "besides that, do you
// have any other income?" chain reads it, which is what makes it a safe seed for the follow-up closure below —
// unlike the `type_40_x` flags, which the EXPENSE questions gate on too.
const INCOME_COUNT_FACT = 'income_types_total';

// Expenses are out of scope for this kind by design (they are one flat-rate CODE per section, not per income line),
// so the closure must never walk into them — nor into anything gated on one of THEIR keys.
const EXPENSE_PREFIX = 'EXP_';

/** Which FieldKeys an item's VisibleWhen actually reads. Falls back to a token scan for expressions the parser
 *  calls "advanced" (mixed AND/OR, parentheses) so an unusual gate under-reports rather than mis-reports. */
function gateFields(row) {
  const raw = String((row && row.visibleWhen) || '').trim();
  if (!raw || /^always$/i.test(raw)) return [];
  const parsed = parseVisibleWhen(raw);
  if (parsed.mode === 'conditional') {
    return parsed.conditions.map((c) => String((c && c.field) || '').trim().toLowerCase()).filter(Boolean);
  }
  return raw.toLowerCase().split(/[^a-z0-9_()]+/).filter(Boolean);
}

/**
 * Everything one INCOME_CLASSIFY owns in the builder, in two flavours:
 *
 *  - **target** — the §40 row its catalog writes to (`incomeFieldKey` / `periodFieldKey`).
 *  - **follow-up** — the "do you have other income?" chain. `HAS_OTHER_INCOME_40(1)` is gated on this driver's own
 *    count fact, and `INCOME_OTHER_TYPES` on that item's answer, so the chain is found by closure rather than named
 *    in config: it IS the driver's question being put a second time, for the customer who reported only one
 *    category, and listing it outside made the section read as if a second classifier ran the show.
 *
 * 🔴 The closure walks FieldKeys, never the `type_40_x` flags — every EXP_OPTION_40_x is gated on those, and pulling
 *    the expense questions in here would claim a whole block this kind is explicitly not responsible for.
 */
function ownedRowsForDriver(driver, rows, alreadyOwned) {
  const targets = new Set();
  (driver.incomeCategories || []).forEach((c) => {
    [c && c.incomeFieldKey, c && c.periodFieldKey].forEach((k) => {
      const key = String(k || '').trim().toUpperCase();
      if (key) targets.add(key);
    });
  });

  const eligible = rows.filter((r) => r !== driver
    && !alreadyOwned.has(r._uid)
    && !String(r.fieldKey || '').trim().toUpperCase().startsWith(EXPENSE_PREFIX));

  const mine = new Map();
  eligible.forEach((r) => {
    if (targets.has(String(r.fieldKey || '').trim().toUpperCase())) mine.set(r._uid, 'target');
  });

  // Seed the chain on the count fact, then keep pulling in whatever reads a key already claimed.
  const claimedKeys = new Set();
  const claim = (row) => {
    const key = String(row.fieldKey || '').trim().toLowerCase();
    if (key) claimedKeys.add(key);
  };
  eligible.forEach((r) => {
    if (mine.has(r._uid)) return;
    if (gateFields(r).includes(INCOME_COUNT_FACT)) { mine.set(r._uid, 'follow-up'); claim(r); }
  });
  for (let pass = 0; pass < rows.length; pass += 1) {
    let grew = false;
    eligible.forEach((r) => {
      if (mine.has(r._uid)) return;
      if (gateFields(r).some((f) => claimedKeys.has(f))) { mine.set(r._uid, 'follow-up'); claim(r); grew = true; }
    });
    if (!grew) break;
  }

  // Form order, so the nested list reads the way the walk does.
  return rows.filter((r) => mine.has(r._uid)).map((r) => ({ row: r, why: mine.get(r._uid) }));
}

// ── pre-save diff (ported from V1) ────────────────────────────────────────
// The stable identity of a V2 item: its kind + section + what it binds (FieldKey /
// classifier flag keys), falling back to its label.
function tqBoundKeys(item) {
  const kind = String(item.itemKind || '').trim().toUpperCase();
  if (kind === 'INCOME_CLASSIFIER') {
    return (item.incomeCategories || [])
      .map((c) => 'C:' + String(c.flagKey || c.code || '').trim())
      .filter((k) => k !== 'C:')
      .sort();
  }
  // 🔴 A SHORT_FORM binds BOTH: its own bookkeeping FieldKey and the sibling FIELD_INPUT keys
  //    its boxes write into — so both belong in its identity. The members alone would let two
  //    cards collecting the same amounts collapse into one diff entry, and the own key alone
  //    collapses every card the author has not keyed in yet (they all fall back to the label).
  //    Members are sorted so reordering rows in the card never reads as a different question.
  if (kind === 'SHORT_FORM') {
    const own = String(item.fieldKey || '').trim();
    const members = shortFormFieldKeys(item).map((k) => 'S:' + k).sort();
    return (own ? ['F:' + own] : []).concat(members);
  }
  const f = String(item.fieldKey || '').trim();
  return f ? ['F:' + f] : [];
}

function tqNaturalKey(item) {
  const kind = String(item.itemKind || '').trim().toUpperCase();
  const group = String(item.groupCode || '').trim().toUpperCase();
  const bound = tqBoundKeys(item);
  const sig = bound.length
    ? bound.join(',')
    : ('L:' + String(item.label || item.questionTextTh || '').trim().toLowerCase());
  return `${kind}|${group}|${sig}`;
}

// Content signature — the canonical request payload minus its volatile sortOrder
// (forced to 0), so a pure reorder does not read as an edit.
function tqContentSig(item) {
  return JSON.stringify(taxQuestionV2Store.buildItemRequest(item, 0));
}

// Best-effort classify current vs baseline by natural key: added (new key),
// edited (same key, changed content), removed (baseline key gone).
function diffByNaturalKey(baseline, current, keyFn, sigFn) {
  const baseMap = new Map();
  (baseline || []).forEach((it) => {
    const k = keyFn(it);
    if (!baseMap.has(k)) baseMap.set(k, it);
  });
  const curMap = new Map();
  (current || []).forEach((it) => {
    const k = keyFn(it);
    if (!curMap.has(k)) curMap.set(k, it);
  });
  const added = [];
  const edited = [];
  const removed = [];
  curMap.forEach((it, k) => {
    if (!baseMap.has(k)) added.push(it);
    else if (sigFn(it) !== sigFn(baseMap.get(k))) edited.push(it);
  });
  baseMap.forEach((it, k) => {
    if (!curMap.has(k)) removed.push(it);
  });
  return { added, edited, removed };
}

export default {
  name: 'TaxQuestionV2Edit',
  components: {
    TaxQuestionItemV2Dialog, TaxQuestionHistoryV2Dialog, TaxCardInlinePreviewV2, draggable, ConfirmDeleteDialog,
  },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      loading: false,
      saving: false,
      dirty: false,
      // true once the header SAVE has scrolled out of view (see onWindowScroll)
      floatingSaveVisible: false,
      configLabel: '',
      versionCode: '',
      versionActive: false,
      // Question version currently materialized in the builder (guards a redundant
      // reload when the route swaps `new` → the freshly-created id after SAVE).
      loadedVersionId: null,
      // Add-mode metadata form (new question version) — created on SAVE.
      newVersionCode: '',
      newDescription: '',
      newResumePromptTh: '',
      knowledgeCatalog: [],
      categoryCatalog: [],
      dialogOpen: false,
      dialogInitial: null,
      addSectionDialog: false,
      newSectionName: '',
      expandedSettings: {},
      // Section collapse — default COLLAPSED (#649: ทุกหมวดหมู่เริ่มแบบ "หุบ"); only an explicit `true` expands it.
      expandedSections: {},
      // Pre-save confirm: baseline snapshot of the loaded item set + its diff.
      baseline: [],
      confirmSaveDialog: false,
      // บันทึกช่วยจำที่จะติดไปกับแถวประวัติของการเซฟรอบนี้ (ล้างทุกครั้งที่เปิด dialog ใหม่)
      saveComment: '',
      liveConfirmChecked: false,
      saveDiff: { added: [], edited: [], removed: [] },
      // Save-history dialog + the "you still have unsaved edits" gate in front of it.
      historyDialog: false,
      historyDirtyDialog: false,
      // Question version created by an add-mode SAVE whose follow-up bulk save failed —
      // makes the retry idempotent (never re-creates the same versionCode).
      pendingNewVersionId: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    configVersionId() {
      const id = Number(this.$route.params.configVersionId);
      return Number.isFinite(id) ? id : null;
    },
    // ADD mode is signalled by the literal `new` route param.
    isNewVersion() {
      return this.$route.params.questionVersionId === 'new';
    },
    questionVersionId() {
      if (this.isNewVersion) return null;
      const id = Number(this.$route.params.questionVersionId);
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    items() {
      return taxQuestionV2Store.state.items;
    },
    warnings() {
      return taxQuestionV2Store.state.warnings;
    },
    /**
     * The "แสดงตัวอย่างการ์ด" switch. 🔴 Read and written straight through to the store, which
     * persists it — deliberately NOT in `data()` (a local copy would reset on every navigation and
     * silently disagree with the persisted value), and deliberately NOT touching `this.dirty`: a
     * display preference is not an unsaved edit.
     */
    showCardPreview: {
      get() {
        return taxQuestionV2Store.state.showCardPreview;
      },
      set(on) {
        taxQuestionV2Store.setShowCardPreview(on);
      },
    },
    headerLabel() {
      const cfg = this.configLabel || `Config #${this.configVersionId}`;
      if (this.isNewVersion) return `ชุดคำถามใหม่ (${cfg})`;
      return this.versionCode ? `${cfg} · ${this.versionCode}` : cfg;
    },
    orderedItems() {
      return this.sections.flatMap((g) => g.rows);
    },
    sections() {
      const itemsByCode = new Map();
      this.items.forEach((item) => {
        const code = (item.groupCode && String(item.groupCode).trim().toUpperCase()) || 'GENERAL';
        if (!itemsByCode.has(code)) itemsByCode.set(code, []);
        itemsByCode.get(code).push(item);
      });
      const metaMap = taxQuestionV2Store.state.groupMeta;
      const codes = new Set(Object.keys(metaMap));
      itemsByCode.forEach((_, code) => {
        if (code !== 'GENERAL') codes.add(code);
      });
      if (itemsByCode.has('GENERAL')) codes.add('GENERAL');
      const list = Array.from(codes).map((code) => {
        const meta = metaMap[code] || null;
        const rows = (itemsByCode.get(code) || [])
          .slice()
          .sort((a, b) => (a.sortOrder - b.sortOrder) || ((a.questionItemID || 0) - (b.questionItemID || 0)));
        // 🔴 An INCOME_CLASSIFY drives the §40 rows its catalog points at — they are never put to the customer on
        //    their own while it is in play, and listing them as peers made the section read as thirteen unrelated
        //    questions when it is really one conversation plus its landing rows. They move INSIDE the driver's row.
        //    They are NOT removed from `rows`: that list is what the save payload and the sortOrder maths walk, and
        //    dropping a row from it would delete the item the amounts land on.
        const ownedByUid = {};
        const owned = new Set();
        rows.filter((r) => String(r.itemKind || '').trim().toUpperCase() === 'INCOME_CLASSIFY').forEach((driver) => {
          const mine = ownedRowsForDriver(driver, rows, owned);
          mine.forEach((o) => owned.add(o.row._uid));
          ownedByUid[driver._uid] = mine;
        });
        return {
          code,
          label: (meta && meta.label) || taxQuestionV2Store.groupLabel(code),
          meta,
          rows,
          // What the table lists. Everything a driver owns is reachable through that driver instead.
          visibleRows: rows.filter((r) => !owned.has(r._uid)),
          ownedByUid,
          sortOrder: meta ? (Number(meta.sortOrder) || 0) : 0,
        };
      });
      list.sort((a, b) => (a.sortOrder - b.sortOrder) || a.code.localeCompare(b.code));

      // Running start for each section's Order column, so numbering carries on across sections
      // (part 2 continues from part 1) instead of restarting at 1 in every table.
      // 🔴 Assigned AFTER the sort, and derived from visibleRows — the same list the table renders.
      //    Computed before sorting it would number the sections in the wrong order, and counting
      //    `rows` instead would leave gaps, because rows owned by an INCOME_CLASSIFY driver are
      //    displayed inside that driver's row rather than as table rows of their own.
      let seq = 0;
      list.forEach((g) => {
        g.seqOffset = seq;
        seq += g.visibleRows.length;
      });

      return list;
    },
    draggableSections: {
      get() {
        return this.sections;
      },
      set(newOrder) {
        (newOrder || []).forEach((sec, idx) => {
          if (sec && sec.meta) sec.meta.sortOrder = idx + 1;
        });
        this.dirty = true;
      },
    },
  },
  watch: {
    // Route-param swap (e.g. add-mode → the created version) re-materializes the
    // builder; skipped when that version is already loaded.
    questionVersionId(id) {
      if (id != null && id === this.loadedVersionId) return;
      this.loadBuilder();
    },
    configVersionId() {
      this.resolveConfigLabel();
    },
  },
  mounted() {
    taxQuestionV2Store.setConfigVersion(this.configVersionId);
    this.resolveConfigLabel();
    this.loadKnowledgeCatalog();
    this.loadBuilder();
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    window.addEventListener('resize', this.onWindowScroll);
    // Entering the route already scrolled (browser back / restored position) must not leave the
    // page with no SAVE in reach.
    this.$nextTick(this.onWindowScroll);
    // macOS overlay scrollbars stay invisible until an active scroll gesture, which made this
    // long page look unscrollable. Force the window scrollbar to render always-on while this
    // page is mounted (see the unscoped html.tq2-scrollbar rules below).
    document.documentElement.classList.add('tq2-scrollbar');
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('scroll', this.onWindowScroll);
    window.removeEventListener('resize', this.onWindowScroll);
    document.documentElement.classList.remove('tq2-scrollbar');
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('ละทิ้งการแก้ไขที่ยังไม่บันทึก?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    /**
     * Does this item kind draw a card? A plain method, NOT a computed: the table already runs
     * `sections` + `ownedRowsForDriver()` over every row on each render, and a per-row computed would
     * add another pass over the same list for a four-item lookup.
     */
    hasCardPreview(kind) {
      return itemKindHasCardPreview(kind);
    },
    onBeforeUnload(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    /**
     * Show the floating SAVE only once the header one is off screen, so the two are never
     * both visible. Measured off the real button rather than a magic scroll offset — the
     * header grows a LIVE banner and an add-mode block, so its height is not a constant.
     * Falls back to a plain scroll threshold if the ref is not mounted yet.
     */
    onWindowScroll() {
      const ref = this.$refs.headerSave;
      const el = ref && ref.$el ? ref.$el : ref;
      if (!el || typeof el.getBoundingClientRect !== 'function') {
        this.floatingSaveVisible = window.scrollY > 240;
        return;
      }
      this.floatingSaveVisible = el.getBoundingClientRect().bottom < 8;
    },
    async resolveConfigLabel() {
      if (this.configVersionId == null) return;
      try {
        const version = await taxConfigV2Api.getVersion(this.configVersionId);
        if (!version) return;
        let yearCode = `Year#${version.yearID}`;
        try {
          const year = await taxConfigV2Api.getYear(version.yearID);
          if (year && year.taxYearCode) yearCode = year.taxYearCode;
        } catch {
          // best-effort year label
        }
        this.configLabel = `${yearCode} / ${version.versionCode}`;
      } catch {
        // best-effort label only
      }
    },
    async loadKnowledgeCatalog() {
      // Best-effort MetaDoc catalog; leave empty when unavailable (non-fatal).
      try {
        const docs = await metaDocApi.getAvailableDocuments();
        this.knowledgeCatalog = (docs || [])
          .map((d) => {
            const code = d.vectorDbName;
            const parts = [d.itemID, d.nameEN].filter((p) => p && String(p).trim());
            return { knowledgeCode: code, title: parts.length ? parts.join(' — ') : code };
          })
          .filter((k) => k.knowledgeCode);
        // Section pickers offer SUBCATEGORIES (DocumentCategory → Title, e.g. Tax → Personal Income Tax)
        // rather than individual docs — derived from the same payload so the list only shows subcategories
        // that actually have available metadocs. The value is the "Category:Title" token the backend
        // resolver parses (bare "Category" would mean the whole category).
        const subcats = new Map();
        (docs || []).forEach((d) => {
          const cat = (d.documentCategory || '').trim();
          const sub = (d.title || '').trim();
          if (!cat || !sub) return;
          const value = `${cat}:${sub}`;
          if (!subcats.has(value)) subcats.set(value, { value, title: `${cat} — ${sub}` });
        });
        this.categoryCatalog = [...subcats.values()].sort((a, b) => a.title.localeCompare(b.title));
      } catch {
        this.knowledgeCatalog = [];
        this.categoryCatalog = [];
      }
    },
    // Suggest the next version code (Q{n+1}); falls back to Q1 on error. The only
    // remaining reason this page reads the version list — lifecycle lives in the tree.
    async computeNextVersionCode() {
      try {
        const list = await taxQuestionV2Api.getVersions(this.configVersionId, true);
        const rows = Array.isArray(list) ? list : [];
        // Skip codes already taken (a deleted Q2 must not make the next code collide).
        const used = new Set(rows.map((q) => String(q.versionCode || '').trim().toUpperCase()));
        let n = rows.length + 1;
        while (used.has(`Q${n}`)) n += 1;
        return `Q${n}`;
      } catch {
        return 'Q1';
      }
    },
    async loadBuilder() {
      taxQuestionV2Store.setConfigVersion(this.configVersionId);
      if (this.configVersionId == null) {
        this.showError('Config version ไม่ถูกต้อง');
        this.$router.replace('/TaxConfigurationV2');
        return;
      }

      // ── ADD mode: blank builder + prefilled new-version metadata. ──
      if (this.isNewVersion) {
        this.loading = true;
        taxQuestionV2Store.clearBuilder();
        this.loadedVersionId = null;
        this.pendingNewVersionId = null;
        try {
          this.versionCode = '';
          this.versionActive = false;
          this.newDescription = '';
          this.newResumePromptTh = '';
          this.newVersionCode = await this.computeNextVersionCode();
          this.renumber();
          this.snapshotBaseline();
          this.dirty = false;
        } catch (err) {
          this.showError(err?.message || 'เริ่มชุดคำถามใหม่ไม่สำเร็จ');
        } finally {
          this.loading = false;
        }
        return;
      }

      if (this.questionVersionId == null) {
        this.showError('ไม่พบชุดคำถาม');
        this.$router.replace('/TaxConfigurationV2');
        return;
      }
      this.loading = true;
      taxQuestionV2Store.clearBuilder();
      try {
        const full = await taxQuestionV2Api.getFull(this.questionVersionId, true);
        if (!full || !full.version) {
          this.showError('ไม่พบชุดคำถาม');
          this.$router.replace('/TaxConfigurationV2');
          return;
        }
        taxQuestionV2Store.loadFull(full);
        this.versionCode = full.version.versionCode || '';
        this.versionActive = Boolean(full.version.isActive);
        this.ensureStepsForGroups();
        this.normalizeSectionOrder();
        this.renumber();
        this.snapshotBaseline();
        this.dirty = false;
        this.loadedVersionId = this.questionVersionId;
        this.pendingNewVersionId = null;
      } catch (err) {
        if (err && (err.httpStatus === 404 || err.status === 6)) {
          this.showError('ไม่พบชุดคำถาม');
          this.$router.replace('/TaxConfigurationV2');
          return;
        }
        this.showError(err?.message || 'โหลด builder ไม่สำเร็จ');
      } finally {
        this.loading = false;
      }
    },
    // ── builder helpers (lifted from V1) ──
    renumber() {
      let n = 0;
      this.sections.forEach((group) => {
        group.rows.forEach((row) => {
          n += 1;
          row.sortOrder = n;
        });
      });
    },
    normalizeSectionOrder() {
      this.sections.forEach((sec, idx) => {
        if (sec.meta) sec.meta.sortOrder = idx + 1;
      });
    },
    ensureStepsForGroups() {
      taxQuestionV2Store.ensureGroupMetaForCodes(this.sections.map((g) => g.code));
    },
    maxSortOrder() {
      return this.items.reduce((max, it) => Math.max(max, Number(it.sortOrder) || 0), 0);
    },
    // 🔴 Both reorder paths renumber through `visibleRows`, and a driver drags its owned §40 rows WITH it. Those
    //    rows are not in the table any more, so renumbering by the table's index alone would leave them stranded at
    //    whatever number they had — a §40 row that sorts BEFORE its driver is asked as its own question first, which
    //    is exactly the interleaving this kind exists to remove. Keeping them immediately behind the driver also
    //    keeps every sortOrder contiguous, so nothing else in the builder has to know they are nested.
    // 🔴 NOT `renumber()` — that one exists, walks every group, and is called after add/delete/import. Shadowing it
    //    would have renumbered one section on every one of those paths and left the rest at their old numbers.
    renumberVisible(group) {
      if (!group || !Array.isArray(group.visibleRows)) return;
      let n = (group.visibleRows[0] && Number(group.visibleRows[0].sortOrder) > 0)
        ? Math.min(...group.rows.map((r) => Number(r.sortOrder) || 0).filter((v) => v > 0)) - 1
        : 0;
      group.visibleRows.forEach((row) => {
        row.sortOrder = ++n;
        (group.ownedByUid[row._uid] || []).forEach((o) => { o.row.sortOrder = ++n; });
      });
      this.dirty = true;
    },
    moveItem(group, idx, dir) {
      const target = idx + dir;
      const list = group.visibleRows;
      if (target < 0 || target >= list.length) return;
      // Swap in the VISIBLE list, then renumber — a plain sortOrder swap cannot express "and bring your children".
      const reordered = list.slice();
      const [moved] = reordered.splice(idx, 1);
      reordered.splice(target, 0, moved);
      group.visibleRows = reordered;
      this.renumberVisible(group);
    },
    onItemReorder(group) {
      this.renumberVisible(group);
    },
    moveSection(idx, dir) {
      const list = this.sections;
      const target = idx + dir;
      if (target < 0 || target >= list.length) return;
      const a = list[idx].meta;
      const b = list[target].meta;
      if (!a || !b) return;
      const tmp = a.sortOrder;
      a.sortOrder = b.sortOrder;
      b.sortOrder = tmp;
      this.dirty = true;
    },
    openAddSection() {
      this.newSectionName = '';
      this.addSectionDialog = true;
    },
    confirmAddSection() {
      const name = String(this.newSectionName || '').trim();
      if (!name) {
        this.showError('กรุณากรอกชื่อ Section');
        return;
      }
      const code = taxQuestionV2Store.createSection(name);
      this.normalizeSectionOrder();
      this.expandedSettings = { ...this.expandedSettings, [code]: false };
      // A section the user just created opens expanded so its empty body / add-item footer is visible.
      this.expandedSections = { ...this.expandedSections, [code]: true };
      this.dirty = true;
      this.addSectionDialog = false;
      this.showSuccess('เพิ่ม Section แล้ว — กด SAVE เพื่อบันทึก');
    },
    async deleteSection(code, label) {
      const ok = await this.$refs.confirmDelete.ask({
        message: 'ต้องการลบ Section นี้และคำถามทั้งหมดในนั้นใช่หรือไม่?',
        target: label ? `${code} - ${label}` : code,
        note: 'การลบจะมีผลเมื่อกด SAVE',
      });
      if (!ok) return;
      taxQuestionV2Store.removeSection(code);
      const next = { ...this.expandedSettings };
      delete next[code];
      this.expandedSettings = next;
      const nextExpanded = { ...this.expandedSections };
      delete nextExpanded[code];
      this.expandedSections = nextExpanded;
      this.normalizeSectionOrder();
      this.renumber();
      this.dirty = true;
      this.showSuccess('ลบ Section แล้ว — กด SAVE เพื่อบันทึก');
    },
    toggleSettings(code) {
      this.expandedSettings = { ...this.expandedSettings, [code]: !this.expandedSettings[code] };
    },
    // Section collapse — default COLLAPSED (#649); only an explicit `true` expands it.
    isSectionExpanded(code) {
      return this.expandedSections[code] === true;
    },
    toggleSection(code) {
      this.expandedSections = { ...this.expandedSections, [code]: !this.isSectionExpanded(code) };
    },
    onSectionLabel(code, value) {
      const meta = taxQuestionV2Store.groupMetaFor(code);
      if (meta) {
        meta.label = value ?? '';
        this.dirty = true;
      }
    },
    updateMeta(code, field, value) {
      taxQuestionV2Store.updateGroupMeta(code, { [field]: value });
      this.dirty = true;
    },
    onStepKnowledge(code, field, ids) {
      taxQuestionV2Store.setGroupKnowledge(code, field, ids);
      this.dirty = true;
    },
    addItemToGroup(groupCode, itemKind = 'FIELD_INPUT') {
      this.dialogInitial = taxQuestionV2Store.createBlankItem({
        itemKind,
        groupCode: groupCode === 'GENERAL' ? null : groupCode,
        sortOrder: this.maxSortOrder() + 1,
      });
      this.dialogOpen = true;
    },
    openEditDialog(row) {
      this.dialogInitial = JSON.parse(JSON.stringify(row));
      this.dialogOpen = true;
    },
    onDialogSubmit(submitted) {
      const existingIdx = this.items.findIndex((it) => it._uid === submitted._uid);
      if (existingIdx >= 0) {
        Object.assign(this.items[existingIdx], submitted);
      } else {
        if (submitted.sortOrder == null || submitted.sortOrder === 0) {
          submitted.sortOrder = this.maxSortOrder() + 1;
        }
        this.items.push(submitted);
      }
      this.ensureStepsForGroups();
      this.renumber();
      this.dirty = true;
      this.dialogOpen = false;
      this.dialogInitial = null;
      this.showSuccess(existingIdx >= 0 ? 'อัปเดตรายการแล้ว — กด SAVE เพื่อบันทึก' : 'เพิ่มรายการแล้ว — กด SAVE เพื่อบันทึก');
    },
    async deleteItem(row) {
      const ok = await this.$refs.confirmDelete.ask({
        message: 'ต้องการลบคำถามนี้ใช่หรือไม่?',
        target: this.diffItemLabel(row),
        note: 'การลบจะมีผลเมื่อกด SAVE',
      });
      if (!ok) return;
      const idx = this.items.findIndex((it) => it._uid === row._uid);
      if (idx >= 0) {
        this.items.splice(idx, 1);
        this.renumber();
        this.dirty = true;
        this.showSuccess('ลบรายการแล้ว — กด SAVE เพื่อบันทึก');
      }
    },
    // Snapshot the loaded item set as the diff baseline (deep clone so the working
    // copy can mutate freely). Called after every load / re-sync.
    snapshotBaseline() {
      this.baseline = JSON.parse(JSON.stringify(this.items || []));
    },
    // Best-effort "what changed" summary of the working set vs baseline.
    computeSaveDiff() {
      return diffByNaturalKey(this.baseline, this.items, tqNaturalKey, tqContentSig);
    },
    // A readable one-line label for a diffed question in the confirm dialog.
    diffItemLabel(item) {
      return String(item.questionTextTh || item.label || '').trim() || item.fieldKey || '(ไม่มีชื่อ)';
    },
    // SAVE opens the confirm dialog with the change summary + (when LIVE) an
    // explicit acknowledgement; the bulk save only runs on confirm.
    saveAll() {
      if (!this.isNewVersion && this.questionVersionId == null) {
        this.showError('ไม่พบชุดคำถาม — บันทึกไม่ได้');
        return;
      }
      this.saveDiff = this.computeSaveDiff();
      this.liveConfirmChecked = false;
      this.saveComment = '';           // ข้อความของรอบก่อนไม่ควรติดมากับรอบนี้
      this.confirmSaveDialog = true;
    },
    async confirmSave() {
      this.confirmSaveDialog = false;
      // ADD mode: create the version first, then save its steps + items.
      if (this.isNewVersion) {
        await this.saveNewVersion();
        return;
      }
      await this.commitSave();
    },
    async commitSave() {
      if (this.questionVersionId == null) {
        this.showError('ไม่พบชุดคำถาม — บันทึกไม่ได้');
        return;
      }
      this.saving = true;
      try {
        const items = this.orderedItems.map((item, idx) => taxQuestionV2Store.buildItemRequest(item, idx + 1));
        const steps = taxQuestionV2Store.buildSteps(this.sections.map((g) => g.code));
        const result = await taxQuestionV2Api.bulkSaveItems(
          this.questionVersionId, { steps, items, comment: this.saveComment });
        // The re-sync is best-effort: a missing payload must never turn a committed
        // save into a "save failed" (guarded exactly like loadBuilder).
        const full = await taxQuestionV2Api.getFull(this.questionVersionId, true);
        if (full && full.version) {
          taxQuestionV2Store.loadFull(full);
          this.versionCode = full.version.versionCode || this.versionCode;
          this.versionActive = Boolean(full.version.isActive);
        }
        this.ensureStepsForGroups();
        this.renumber();
        this.snapshotBaseline();
        this.dirty = false;
        const count = Array.isArray(result) ? result.length : this.items.length;
        if (this.warnings.length) {
          this.showWarning(`บันทึก ${count} รายการแล้ว (มี ${this.warnings.length} คำเตือนสูตรคำนวณ)`);
        } else {
          this.showSuccess(`บันทึก ${count} รายการแล้ว`);
        }
      } catch (err) {
        this.showError(err?.message || 'บันทึกไม่สำเร็จ');
      } finally {
        this.saving = false;
      }
    },
    // ── Save history ───────────────────────────────────────────────────────────
    // A restore overwrites the whole question set with the picked round, so unsaved edits in the
    // builder would be lost without a word. Warn first; only an explicit "open" gets through.
    openHistory() {
      if (this.isNewVersion || this.questionVersionId == null) return;
      if (this.dirty) {
        this.historyDirtyDialog = true;
        return;
      }
      this.historyDialog = true;
    },
    confirmOpenHistory() {
      this.historyDirtyDialog = false;
      this.historyDialog = true;
    },
    // The dialog already did the restore (and closed its own confirm + reloaded its table).
    // All that is left here is to pull the freshly-written tree back into the builder, using the
    // EXACT post-save sequence of commitSave() — anything less leaves the diff baseline stale.
    async onHistoryRestored(result) {
      if (this.questionVersionId == null) return;
      this.loading = true;
      try {
        const full = await taxQuestionV2Api.getFull(this.questionVersionId, true);
        if (full && full.version) {
          taxQuestionV2Store.loadFull(full);
          this.versionCode = full.version.versionCode || this.versionCode;
          this.versionActive = Boolean(full.version.isActive);
        }
        this.ensureStepsForGroups();
        this.renumber();
        this.snapshotBaseline();
        this.dirty = false;

        const from = result && result.restoredFromNo;
        const label = from ? `กู้คืนจาก v${from} เรียบร้อย` : 'กู้คืนเวอร์ชันเรียบร้อย';
        const count = Number(
          result && result.itemCount != null ? result.itemCount : this.items.length,
        );
        const warns = result && Array.isArray(result.warnings) ? result.warnings : [];
        // One snackbar per turn — a success call followed by a warning call would only flash.
        if (warns.length) {
          this.showWarning(`${label} (${count} รายการ) — ${warns.join(' | ')}`);
        } else {
          this.showSuccess(`${label} (${count} รายการ)`);
        }
      } catch (err) {
        // The restore itself already committed on the server; only the re-sync failed.
        this.showError(err?.message || 'กู้คืนแล้ว แต่โหลดข้อมูลใหม่ไม่สำเร็จ — กรุณารีเฟรชหน้า');
      } finally {
        this.loading = false;
      }
    },
    // ADD mode SAVE: create the (inactive) version → bulk-save the built steps +
    // items under its new id → re-sync → become edit mode. Active state and the
    // rest of the version lifecycle live in the Tax Configuration V2 tree.
    async saveNewVersion() {
      const code = String(this.newVersionCode || '').trim().toUpperCase();
      if (!code) {
        this.showError('กรุณากรอก Version code');
        return;
      }
      if (this.configVersionId == null) {
        this.showError('Config version ไม่ถูกต้อง — บันทึกไม่ได้');
        return;
      }
      this.saving = true;
      try {
        // Idempotent retry: a previous attempt may already have created the version
        // (only its item bulk-save failed) — re-creating it would be rejected as a
        // duplicate versionCode and strand the built content.
        let newId = this.pendingNewVersionId;
        if (newId == null) {
          const desc = String(this.newDescription || '').trim();
          const resume = String(this.newResumePromptTh || '').trim();
          const created = await taxQuestionV2Api.createVersion({
            taxConfigVersionID: this.configVersionId,
            versionCode: code,
            description: desc || null,
            resumePromptTh: resume || null,
            isActive: false,
          });
          newId = created && created.questionVersionID;
          if (newId == null) throw new Error('สร้างชุดคำถามไม่สำเร็จ (ไม่พบ id)');
          this.pendingNewVersionId = newId;
        }
        const items = this.orderedItems.map((item, idx) => taxQuestionV2Store.buildItemRequest(item, idx + 1));
        const steps = taxQuestionV2Store.buildSteps(this.sections.map((g) => g.code));
        if (items.length || steps.length) {
          await taxQuestionV2Api.bulkSaveItems(newId, { steps, items, comment: this.saveComment });
        }
        const full = await taxQuestionV2Api.getFull(newId, true);
        if (full && full.version) {
          taxQuestionV2Store.loadFull(full);
          this.versionCode = full.version.versionCode || code;
          this.versionActive = Boolean(full.version.isActive);
        } else {
          this.versionCode = code;
        }
        this.ensureStepsForGroups();
        this.normalizeSectionOrder();
        this.renumber();
        this.snapshotBaseline();
        this.dirty = false;
        this.loadedVersionId = newId;
        this.pendingNewVersionId = null;
        this.showSuccess(`สร้างชุดคำถาม ${code} แล้ว`);
        this.$router.replace(`/TaxQuestionV2/${this.configVersionId}/${newId}`);
      } catch (err) {
        this.showError(err?.message || 'สร้างชุดคำถามไม่สำเร็จ');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/TaxConfigurationV2');
    },
    // ── presentation ──
    visibleWhenLabel(row) {
      return describeVisibleWhen(row && row.visibleWhen);
    },
    isAlwaysVisible(row) {
      const v = String((row && row.visibleWhen) || '').trim();
      return !v || /^always$/i.test(v);
    },
    /**
     * Thai quick-reply labels of a row, for the table's "Quick reply" column.
     *
     * 🔴 Same projection as the item dialog's `chipRows`, deliberately: a chip may be stored as a
     * bare string OR as an object, and an object may carry its text under `label`/`Label` or, for
     * chips authored as {label,value} pairs, only under `value`/`Value`. Reading `label` alone
     * renders blank bullets for perfectly valid rows.
     *
     * Thai only — the runtime shows a chip row in ONE language and falls back to Thai for the whole
     * row unless every chip is translated and none collide, so Thai is what is actually displayed.
     */
    quickReplyLabels(row) {
      const options = Array.isArray(row && row.answerOptions) ? row.answerOptions : [];
      return options
        .map((o) => {
          if (o && typeof o === 'object' && !Array.isArray(o)) {
            return String(o.label ?? o.Label ?? o.value ?? o.Value ?? '').trim();
          }
          return String(o ?? '').trim();
        })
        .filter((label) => label !== '');
    },

    questionPreview(row) {
      const text = String(row.questionTextTh || '').trim();
      if (text) return text;
      return row.label || '(no label)';
    },
    // Compact, comma-joined summary of an INCOME_CLASSIFIER's income categories
    // (labelTh, else code/flagKey) — shown as text + tooltip in the item table.
    incomeCategoryKeys(row) {
      return (row && Array.isArray(row.incomeCategories) ? row.incomeCategories : [])
        .map((c) => String((c && (c.labelTh || c.code || c.flagKey)) || '').trim())
        .filter(Boolean)
        .join(', ');
    },
    // ── INCOME_CLASSIFY summary helpers ─────────────────────────────────────
    // Where each section's amount is written. Shown INSTEAD of the category names an INCOME_CLASSIFIER lists,
    // because for this kind the interesting question in the table is not "which sections exist" (they all do) but
    // "does every section still have a row to land on" — the one thing that breaks when a §40 item is deactivated.
    incomeFanOutList(row) {
      return (row && Array.isArray(row.incomeCategories) ? row.incomeCategories : [])
        .map((c) => String((c && c.incomeFieldKey) || '').trim())
        .filter(Boolean);
    },
    incomeFanOutKeys(row) {
      const keys = this.incomeFanOutList(row);
      if (!keys.length) return '';
      return keys.length > INCOME_TARGET_PREVIEW
        ? `${keys.slice(0, INCOME_TARGET_PREVIEW).join(', ')} …+${keys.length - INCOME_TARGET_PREVIEW}`
        : keys.join(', ');
    },
    incomeFanOutKeysFull(row) {
      return this.incomeFanOutList(row).join(', ');
    },
    // ── CALCULATE summary helpers ───────────────────────────────────────────
    // 🔴 Every access is guarded: rows saved before this kind existed carry no
    // calcConfig at all, and one throw here blanks the whole item table.
    calcFunctionList(row) {
      const cfg = row && row.calcConfig;
      return cfg && Array.isArray(cfg.functions) ? cfg.functions : [];
    },
    calcFunctionCount(row) {
      return this.calcFunctionList(row).length;
    },
    calcFunctionCodeList(row) {
      return this.calcFunctionList(row)
        .map((f) => String((f && (f.functionCode || f.functionId)) || '').trim())
        .filter(Boolean);
    },
    // Preview line: first CALC_CODE_PREVIEW codes, "…" for the rest.
    calcFunctionKeys(row) {
      const codes = this.calcFunctionCodeList(row);
      return codes.length > CALC_CODE_PREVIEW
        ? `${codes.slice(0, CALC_CODE_PREVIEW).join(', ')}, …`
        : codes.join(', ');
    },
    calcFunctionKeysFull(row) {
      return this.calcFunctionCodeList(row).join(', ');
    },
    // ── SHORT_FORM summary helpers ──────────────────────────────────────────
    // 🔴 Guarded like the CALCULATE helpers above, and for the same reason: rows saved
    // before this kind existed carry no shortFormConfig at all, and one throw here blanks
    // the whole item table. That is why the members come from the store's null-safe
    // `shortFormFieldKeys` instead of being read off `row.shortFormConfig.fields` inline —
    // it answers for ANY item without a kind check, already trimmed and de-duped, so the
    // count and the key list below can never disagree with each other.
    shortFormFieldCount(row) {
      return shortFormFieldKeys(row).length;
    },
    // Preview line: first SHORT_FORM_KEY_PREVIEW keys, "…" for the rest.
    shortFormKeys(row) {
      const keys = shortFormFieldKeys(row);
      return keys.length > SHORT_FORM_KEY_PREVIEW
        ? `${keys.slice(0, SHORT_FORM_KEY_PREVIEW).join(', ')}, …`
        : keys.join(', ');
    },
    shortFormKeysFull(row) {
      return shortFormFieldKeys(row).join(', ');
    },
    // Resolve the readable "Type" tag of a row: the non-scalar V2 kinds describe
    // themselves; FIELD_INPUT resolves from its inputType.
    answerKind(row) {
      const kind = String((row && row.itemKind) || '').trim().toUpperCase();
      if (kind === 'CALCULATE') {
        // No input widget at all — the walker runs it and moves on, the customer
        // is never asked, so there is no answer shape to describe.
        return {
          label: 'คำนวณ',
          color: 'deep-purple',
          icon: 'mdi-calculator-variant',
          hint: `จุดคำนวณอัตโนมัติ — ระบบรัน ${this.calcFunctionCount(row)} สูตรตามลำดับที่ตั้งไว้ ไม่ถามลูกค้า`,
        };
      }
      if (kind === 'MESSAGE') {
        // Said on the way past (LLM-phrased) and stamped, never asked — placed last it is
        // the closing line of the questionnaire.
        return {
          label: 'ข้อความแจ้ง',
          color: 'light-green',
          icon: 'mdi-message-text-outline',
          hint: 'ระบบพูดข้อความนี้ให้ลูกค้าฟัง (เรียบเรียงผ่าน LLM) แล้วเดินต่อทันที ไม่ถามลูกค้า · พูดครั้งเดียว · วางท้ายสุด = ประโยคปิดฟอร์ม',
        };
      }
      if (kind === 'USAGE_FUNNEL_STATE') {
        // A /TaxDashboard funnel marker (#746): walked over in silence, logs "passed state X" once per form.
        return {
          label: 'จุดนับ Funnel',
          color: 'blue',
          icon: 'mdi-filter-variant',
          hint: `จุดนับ Usage Funnel — ไม่ถามลูกค้า · เดินผ่านแล้วนับฟอร์มนี้ในขั้น "${this.usageFunnelStateTitle(row)}" ของ TaxDashboard (ครั้งเดียวต่อฟอร์ม)`,
        };
      }
      if (kind === 'END_FORM') {
        // Where the form stops — everything sorted after it is unreachable once its
        // gate opens — but the question itself stays on screen and keeps taking replies.
        return {
          label: 'จบฟอร์ม',
          color: 'error',
          icon: 'mdi-flag-checkered',
          hint: 'เดินมาถึงข้อนี้แล้วฟอร์มถูกปิดทันที (Completed) และไม่ถามข้อถัดไป · คำถามยังค้างบนจอจนกว่าคำตอบจะชัดว่าต้องการเริ่มฟอร์มใหม่',
        };
      }
      if (kind === 'INCOME_CLASSIFIER') {
        const n = (row.incomeCategories || []).length;
        return {
          label: 'จำแนกรายได้',
          color: 'deep-orange',
          icon: 'mdi-shape-outline',
          hint: `จำแนกข้อความรายได้เป็น ${n} ประเภท — ระบบตั้ง flag ต่อประเภทให้อัตโนมัติ`,
        };
      }
      if (kind === 'INCOME_CLASSIFY') {
        // Asked repeatedly, not once: it holds the focus for as long as any income topic is still missing its
        // section or its amount, then hands the walk on. Its own answer row is a checkpoint — the money lands on
        // the §40 rows its catalog names.
        const n = (row.incomeCategories || []).length;
        return {
          label: 'คุมบทสนทนารายได้',
          color: 'deep-orange',
          icon: 'mdi-format-list-numbered',
          hint: `ไล่ถามหมวด → ยอด ทีละรายการตามลำดับที่ลูกค้าพูด ครอบคลุม ${n} หมวด — ยอดไปลงที่ข้อ §40 ปลายทาง ไม่เก็บที่ข้อนี้`,
        };
      }
      if (kind === 'CONFIRM') {
        return {
          label: 'ยืนยัน/สรุป',
          color: 'teal',
          icon: 'mdi-check-decagram-outline',
          hint: 'จุดยืนยัน/สรุป — สรุปคำตอบที่เก็บมาแล้วถามผู้ใช้ให้ยืนยันหรือแก้ไข',
        };
      }
      if (kind === 'SHORT_FORM') {
        // Asked like any other question, but the answer arrives as a whole card: N boxes
        // at once, each landing on the sibling FIELD_INPUT its FieldKey names. The card
        // itself stores no amount — its own FieldKey only marks that the customer got here.
        return {
          label: 'ฟอร์มสั้น',
          color: 'cyan',
          icon: 'mdi-card-text-outline',
          hint: `ฟอร์มสั้น — เก็บ ${this.shortFormFieldCount(row)} ยอดพร้อมกันในการ์ดเดียว แล้วบันทึกลงคำถาม FIELD_INPUT ที่ FieldKey ระบุไว้`,
        };
      }
      const key = (row && row.inputType) || '';
      return (
        ANSWER_KIND_META[key]
        || { label: key || '—', color: 'grey', icon: 'mdi-help-circle-outline', hint: 'ไม่ทราบชนิดคำตอบ' }
      );
    },
    kindColor(kind) {
      return KIND_COLORS[kind] || 'grey';
    },
    /** The dashboard row name a USAGE_FUNNEL_STATE row counts for (#746); a value outside the list shows raw. */
    usageFunnelStateTitle(row) {
      const value = String((row && row.stateGroup) || '').trim();
      if (!value) return 'ยังไม่เลือกขั้น';
      const option = USAGE_FUNNEL_STATE_OPTIONS.find((o) => o.value === value);
      return option ? option.title : value;
    },
    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showWarning(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'warning';
      this.snackbar = true;
    },
    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* Floating SAVE (see the template block of the same name). Sits above page content but well
   below Vuetify's overlay stack (2400+), so every dialog still covers it. */
.tq2-floating-save {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1002;
  display: flex;
  align-items: center;
  gap: 10px;
}

.tq2-fab-enter-active,
.tq2-fab-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.tq2-fab-enter-from,
.tq2-fab-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 600px) {
  .tq2-floating-save {
    right: 16px;
    bottom: 16px;
  }
}

.form-section {
  margin-bottom: 8px;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.tq2-group-card {
  overflow: hidden;
}

.tq2-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: rgba(var(--v-theme-primary), 0.04);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.12);
}

.tq2-group-count {
  margin-left: 12px;
  font-size: 0.8rem;
  color: #6b7280;
  white-space: nowrap;
}

.tq2-section-headrow {
  min-width: 0;
}

.tq2-section-name-field {
  max-width: 360px;
}

.tq2-section-name-field :deep(input) {
  font-weight: 600;
}

.tq2-section-handle {
  cursor: grab;
}

.tq2-section-handle:active {
  cursor: grabbing;
}

/* Sequential number in the Order column. Tabular figures and a fixed min-width keep the drag
   handle on a straight edge when the count crosses into double digits. */
.tq2-qr-list {
  margin: 0;
  padding-left: 0;
  /* Dash markers instead of bullets. `list-style: none` + a ::marker-free layout, because a
     CSS marker cannot be an arbitrary character in every browser this app targets — the dash is
     drawn as real content on each row instead. */
  list-style: none;
}

.tq2-qr-list li {
  display: flex;
  gap: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.75);
  /* Long labels wrap inside the column rather than stretching the table. */
  overflow-wrap: anywhere;
}

.tq2-qr-list li::before {
  content: '-';
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.55);
}

.tq2-item-seq {
  min-width: 18px;
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.55);
  user-select: none;
}

.tq2-item-handle {
  cursor: grab;
}

.tq2-item-handle:active {
  cursor: grabbing;
}

.tq2-empty-section {
  padding: 20px 16px;
  text-align: center;
  color: #9e9e9e;
  font-size: 0.875rem;
}

.tq2-section-footer {
  display: flex;
  justify-content: flex-start;
  padding: 8px 16px 12px;
}

.drag-ghost {
  opacity: 0.5;
}

.drag-chosen {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.4);
}

.tq2-step-meta {
  padding: 12px 16px;
  background: rgba(var(--v-theme-on-surface), 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.tq2-item-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tq2-item-key {
  font-size: 0.8rem;
  color: #6b7280;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* The §40 rows an INCOME_CLASSIFY owns, nested in its cell. Rule on the left, not a bordered card: they belong to
   the row above them and must not read as a second table. */
/* 🔴 `width: 0; min-width: 100%` is the whole reason the table still fits. This block lives in an auto-layout table
   cell, whose width is decided from the intrinsic width of its contents — and ten rows of nowrap question text
   answered that with 2059px against a 1465px page, pushing four columns off the right edge. A zero declared width
   contributes nothing to that calculation; the percentage min-width is ignored while it runs and then makes the
   block fill whatever width the cell actually settled on. */
.tq2-owned {
  width: 0;
  min-width: 100%;
  margin-top: 8px;
  padding: 6px 0 2px 10px;
  border-left: 2px solid rgba(var(--v-theme-on-surface), 0.12);
}

/* The inline card preview, under the question text in the same cell.
   🔴 Same `width: 0; min-width: 100%` trick as `.tq2-owned` above, and for the same reason: a card is far wider
   than a question summary, and in this auto-layout table its intrinsic width would be added to the "Opening
   question" column — pushing Type / Required / Visible when / Action off the right edge of the page (it happened:
   2059px of table on a 1465px screen). Declaring a zero width contributes nothing to that calculation, and the
   percentage min-width then fills whatever width the cell settled on by itself.
   🔴 No height / max-height / overflow: a scroller inside the cell would swallow the wheel over every card. */
.tq2-inline-preview-wrap {
  width: 0;
  min-width: 100%;
}

/* 🔴 The cap and the spacing belong on the CHILD, not on the wrapper:
   · `max-width` on the wrapper would never apply — `min-width` wins over `max-width` in the used-width formula,
     so the card would stretch to the full column on a wide screen instead of staying its designed width;
   · `margin-top` on the wrapper would show up as a gap under EVERY row, because a row whose kind has no card
     still renders the (then empty, zero-height) wrapper. */
.tq2-inline-preview-wrap > :deep(*) {
  min-width: 0;
  max-width: 420px;
  margin-top: 8px;
}

/* Keep the switch its own size in the wrapping header row — a `v-input` in a flex line would otherwise grow and
   push SAVE around as the label renders. */
.tq2-preview-switch {
  flex: 0 0 auto;
}

/* 🔴 `block`, not `flex`. A single-line flex caption cannot wrap, so its min-content is the whole sentence — and in
   an auto-layout table that widened the question column until Type / Required / Visible when / Action were pushed
   off the right edge of the page. */
.tq2-owned-head {
  margin-bottom: 4px;
  font-size: 0.72rem;
  line-height: 1.5;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.tq2-owned-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1px 0;
  font-size: 0.8rem;
}

/* 🔴 Every one of these is `min-width: 0` + ellipsis. A flex child defaults to `min-width: auto`, which is its
   MIN-CONTENT width — and with `nowrap` that is the entire untruncated sentence. Without this the nested rows push
   the question column wide enough to shove Type / Required / Visible when / Action off the right edge of the page. */
.tq2-owned-why {
  flex: 0 0 auto;
  opacity: 0.65;
}

.tq2-owned-why.is-target {
  color: rgb(var(--v-theme-primary));
}

.tq2-owned-why.is-followup {
  color: rgb(var(--v-theme-deep-orange, var(--v-theme-warning)));
}

.tq2-owned-key {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tq2-owned-text {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.78);
}

.tq2-owned-gate {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 30%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* INCOME_CLASSIFIER summary — count chip + truncated key list. */
.tq2-group-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.tq2-group-keys {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

/* Answer-kind tag — soft tonal pill with icon. */
.tq2-answer-kind-chip {
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: 8px;
}

.tq2-answer-kind-chip :deep(.v-chip__prepend) {
  margin-inline-end: 4px;
}

/* Compact bullet list of changed items in the pre-save confirm dialog. */
.tq2-diff-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.tq2-vw-cell {
  font-size: 0.85rem;
}

.tq2-vw-always {
  color: #9e9e9e;
}

.tq2-vw-raw {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
}

.tq2-warning-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.action-btn {
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.delete-btn:hover {
  color: rgb(var(--v-theme-error)) !important;
}
</style>

<!-- Unscoped: the window scrollbar lives on <html>, outside this component's template, so a
     scoped selector can never reach it. The class is toggled by mounted()/beforeUnmount() above
     so the effect only applies while this page is on screen. -->
<style>
html.tq2-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

html.tq2-scrollbar::-webkit-scrollbar {
  width: 10px;
}

html.tq2-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}

html.tq2-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 6px;
}

html.tq2-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(25, 118, 210, 0.5);
}
</style>
